import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react"
import { IconButton, TextField, Box, useTheme, Tooltip, Button, MenuItem } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import "dayjs/locale/es"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { MRT_Localization_ES } from "material-react-table/locales/es"

import { useQuery } from "@tanstack/react-query"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import { Refresh, Visibility, VisibilityOff, SelectAll, Deselect } from "@mui/icons-material"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import Swal from "sweetalert2"

// Configurar dayjs en español
dayjs.locale("es")

// Funciones de conversión de fechas optimizadas
const convertToInputDate = (dateStr) => {
  if (!dateStr) return null

  // Si ya es un objeto dayjs, devolverlo
  if (dayjs.isDayjs(dateStr)) return dateStr

  // Formato dd/mm/yyyy
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = dateStr.split("/")
    return dayjs(`${year}-${month}-${day}`)
  }

  // Formato yyyy-mm-dd
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dayjs(dateStr)
  }

  // Intentar parsear cualquier otro formato
  const parsed = dayjs(dateStr)
  return parsed.isValid() ? parsed : null
}

const convertToDisplayDate = (dateValue) => {
  if (!dateValue) return ""

  try {
    // Hago esto ya que desde el backednd esta llegando la fecha en este formato dd/mm/yyyy
    if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [year, month, day] = dateValue.split("/")
      dateValue = `${day}/${month}/${year}`
    }
  } catch (_) {}

  const dayjsDate = dayjs.isDayjs(dateValue) ? dateValue : dayjs(dateValue)

  if (!dayjsDate.isValid()) return ""

  return dayjsDate.format("DD/MM/YYYY")
}

// Componente optimizado para filtros debounceados
const DebouncedFilter = memo(({ column }) => {
  const [filterValue, setFilterValue] = useState(column.getFilterValue() || "")
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      column.setFilterValue(filterValue || undefined)
    }, 300) // Reducido de 500ms a 300ms

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [filterValue, column])

  return (
    <TextField
      variant="standard"
      placeholder="Filtrar"
      value={filterValue}
      onChange={(e) => setFilterValue(e.target.value)}
      size="small"
      fullWidth
    />
  )
})

// Componente optimizado para campos editables con debounce interno
const EditableCell = memo(({ cell, row, column, editableColumn, currentValue, onFieldChange }) => {
  const [localValue, setLocalValue] = useState(currentValue)
  const timeoutRef = useRef(null)
  const isInitialMount = useRef(true)

  // Sincronizar valor local cuando cambie el valor externo
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    setLocalValue(currentValue)
  }, [currentValue])

  // Debounce para cambios de texto y números
  const debouncedHandleChange = useCallback(
    (newValue) => {
      setLocalValue(newValue)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        onFieldChange(row.original, column.accessorKey, newValue)
      }, 200) // Debounce de 200ms
    },
    [row.original, column.accessorKey, onFieldChange],
  )

  // Handle inmediato para fechas y selects
  const immediateHandleChange = useCallback(
    (newValue) => {
      setLocalValue(newValue)
      onFieldChange(row.original, column.accessorKey, newValue)
    },
    [row.original, column.accessorKey, onFieldChange],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Renderizar campo editable según el tipo
  switch (editableColumn.type) {
    case "text":
      return (
        <TextField
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder={editableColumn.placeholder || ""}
        />
      )
    case "number":
      return (
        <TextField
          type="number"
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          inputProps={{
            step: editableColumn.step || 0.01,
            min: editableColumn.min,
            max: editableColumn.max,
          }}
        />
      )
    case "select":
      return (
        <TextField
          select
          value={localValue || ""}
          onChange={(e) => immediateHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
        >
          {editableColumn.options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )
    case "date":
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            value={convertToInputDate(localValue)}
            onChange={(newValue) => {
              const displayDate = convertToDisplayDate(newValue)
              immediateHandleChange(displayDate)
            }}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                size: "small",
                variant: "standard",
                fullWidth: true,
                placeholder: "dd/mm/yyyy",
              },
            }}
          />
        </LocalizationProvider>
      )
    default:
      return (
        <TextField
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
        />
      )
  }
})

// Componente optimizado para la barra de herramientas
const CustomToolbar = memo(
  ({
    refetch,
    allCurrentlySelected,
    handleToggleSelectAll,
    displayDataLength,
    showOnlySelected,
    setShowOnlySelected,
    selectedDataLength,
    theme,
  }) => (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1 }}>
      <Tooltip title="Recargar datos">
        <IconButton onClick={refetch} size="small">
          <Refresh />
        </IconButton>
      </Tooltip>

      <Button
        variant={allCurrentlySelected ? "contained" : "outlined"}
        color={allCurrentlySelected ? "secondary" : "primary"}
        size="small"
        startIcon={allCurrentlySelected ? <Deselect /> : <SelectAll />}
        onClick={handleToggleSelectAll}
        disabled={displayDataLength === 0}
        sx={{
          minWidth: 160,
          fontWeight: "medium",
          textTransform: "none",
          borderRadius: 2,
          px: 2,
          py: 0.5,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {allCurrentlySelected ? "Deseleccionar todos" : "Seleccionar todos"}
      </Button>

      <Button
        variant={showOnlySelected ? "contained" : "outlined"}
        color={showOnlySelected ? "primary" : "info"}
        size="small"
        startIcon={showOnlySelected ? <Visibility /> : <VisibilityOff />}
        onClick={() => setShowOnlySelected((prev) => !prev)}
        disabled={!showOnlySelected && selectedDataLength === 0}
        sx={(theme) => ({
          minWidth: 180,
          fontWeight: "medium",
          textTransform: "none",
          borderRadius: 2,
          px: 2,
          py: 0.5,
          transition: "all 0.2s ease-in-out",
          position: "relative",
          overflow: "visible",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: theme.shadows[4],
          },
          "&:not(:disabled)": {
            "&::after":
              selectedDataLength > 0 && !showOnlySelected
                ? {
                    content: `"${selectedDataLength}"`,
                    position: "absolute",
                    top: -10,
                    right: -10,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    padding: "2px 8px",
                    minWidth: "22px",
                    height: "22px",
                    whiteSpace: "nowrap",
                  }
                : {},
          },
        })}
      >
        {showOnlySelected ? "Mostrar todos" : "Mostrar seleccionados"}
      </Button>
    </Box>
  ),
)

const LargeScreenTable = memo(
  ({
    theme,
    data,
    columnsTable,
    isLoading,
    isError,
    errorMsgFilterSearch,
    refetch,
    onItemSelected,
    allRecords,
    onUpdateRecord,
    editableColumns = [],
  }) => {
    const [showOnlySelected, setShowOnlySelected] = useState(false)

    // Optimización: usar WeakMap para mejor rendimiento con objetos
    const recordsMap = useMemo(() => {
      const map = new Map()
      allRecords.forEach((record, index) => {
        // Usar un hash más eficiente basado en un ID único si está disponible
        const key = record.originalData.id || JSON.stringify(record.originalData)
        map.set(key, { record, index })
      })
      return map
    }, [allRecords])

    // Función optimizada para manejar cambios en campos editables
    const handleFieldChange = useCallback(
      (originalData, fieldKey, newValue) => {
        const recordKey = originalData.id || JSON.stringify(originalData)
        const recordInfo = recordsMap.get(recordKey)

        if (recordInfo) {
          const { index } = recordInfo

          // Usar una función de actualización para evitar recrear todo el array
          onUpdateRecord(index, (prevRecord) => ({
            ...prevRecord,
            editableFields: {
              ...prevRecord.editableFields,
              [fieldKey]: newValue,
            },
          }))
        }
      },
      [recordsMap, onUpdateRecord],
    )

    const rowSelection = useMemo(() => {
      const selection = {}
      data.forEach((item, index) => {
        const recordKey = item.id || JSON.stringify(item)
        const recordInfo = recordsMap.get(recordKey)
        if (recordInfo?.record?.isSelected) {
          selection[index] = true
        }
      })
      return selection
    }, [data, recordsMap])

    // Columnas optimizadas con memoización mejorada
    const columns = useMemo(
      () =>
        columnsTable.map((col) => {
          const editableColumn = editableColumns.find((ec) => ec.accessorKey === col.accessorKey)

          if (editableColumn) {
            return {
              ...col,
              enableColumnFilter: true,
              Filter: ({ column }) => <DebouncedFilter column={column} />,
              Cell: ({ cell, row }) => {
                const recordKey = row.original.id || JSON.stringify(row.original)
                const recordInfo = recordsMap.get(recordKey)

                if (!recordInfo) return <span>{cell.getValue()}</span>

                const { record } = recordInfo

                // Obtener el valor actual (editado o original)
                const currentValue =
                  record.editableFields[col.accessorKey] !== undefined
                    ? record.editableFields[col.accessorKey]
                    : row.original[col.accessorKey] || editableColumn.defaultValue || ""

                return (
                  <EditableCell
                    cell={cell}
                    row={row}
                    column={col}
                    editableColumn={editableColumn}
                    currentValue={currentValue}
                    onFieldChange={handleFieldChange}
                  />
                )
              },
            }
          }

          // Para columnas no editables
          return {
            ...col,
            enableColumnFilter: true,
            Filter: ({ column }) => <DebouncedFilter column={column} />,
          }
        }),
      [columnsTable, editableColumns, recordsMap, handleFieldChange],
    )

    const selectedData = useMemo(() => {
      return allRecords.filter((record) => record.isSelected).map((record) => record.originalData)
    }, [allRecords])

    const displayData = showOnlySelected ? selectedData : data

    const adjustedRowSelection = useMemo(() => {
      if (!showOnlySelected) return rowSelection

      return selectedData.reduce((acc, _, index) => {
        acc[index] = true
        return acc
      }, {})
    }, [rowSelection, showOnlySelected, selectedData])

    const handleToggleSelectAll = useCallback(() => {
      const currentData = showOnlySelected ? selectedData : data
      const allCurrentlySelected = currentData.every((item) => {
        const recordKey = item.id || JSON.stringify(item)
        const recordInfo = recordsMap.get(recordKey)
        return recordInfo?.record?.isSelected
      })

      const updatedRecords = allRecords.map((record) => {
        const isInCurrentView = currentData.some((item) => {
          const itemKey = item.id || JSON.stringify(item)
          const recordKey = record.originalData.id || JSON.stringify(record.originalData)
          return itemKey === recordKey
        })

        if (isInCurrentView) {
          return { ...record, isSelected: !allCurrentlySelected }
        }
        return record
      })

      onItemSelected(updatedRecords)
    }, [data, selectedData, showOnlySelected, recordsMap, allRecords, onItemSelected])

    const allCurrentlySelected = useMemo(() => {
      const currentData = showOnlySelected ? selectedData : data
      if (currentData.length === 0) return false

      return currentData.every((item) => {
        const recordKey = item.id || JSON.stringify(item)
        const recordInfo = recordsMap.get(recordKey)
        return recordInfo?.record?.isSelected
      })
    }, [showOnlySelected, selectedData, data, recordsMap])

    const onRowSelectionChange = useCallback(
      (updater) => {
        const newSelection = typeof updater === "function" ? updater(adjustedRowSelection) : updater
        const currentDisplayData = showOnlySelected ? selectedData : data

        const updatedRecords = allRecords.map((record) => {
          const displayIndex = currentDisplayData.findIndex((item) => {
            const itemKey = item.id || JSON.stringify(item)
            const recordKey = record.originalData.id || JSON.stringify(record.originalData)
            return itemKey === recordKey
          })

          if (displayIndex !== -1) {
            const isSelected = newSelection[displayIndex] || false
            return { ...record, isSelected }
          }
          return record
        })

        onItemSelected(updatedRecords)
      },
      [onItemSelected, showOnlySelected, selectedData, data, adjustedRowSelection, allRecords],
    )

    const table = useMaterialReactTable({
      columns,
      data: displayData,
      enableRowVirtualization: true,
      getRowVirtualizerOptions: { overscan: 10, estimateSize: () => 60 }, // Optimizado
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableSelectAll: false,

      onRowSelectionChange,

      getRowId: useCallback((row, index) => index.toString(), []),

      state: {
        isLoading,
        rowSelection: adjustedRowSelection,
      },

      initialState: {
        showColumnFilters: true,
        density: "compact",
        pagination: {
          pageSize: 50, // Aumentado para mejor rendimiento
          pageIndex: 0,
        },
      },

      localization: MRT_Localization_ES,
      muiToolbarAlertBannerProps: isError ? { color: "error", children: errorMsgFilterSearch } : undefined,

      muiTableContainerProps: {
        sx: {
          maxHeight: "70vh",
          overflow: "auto",
        },
      },

      muiTableBodyRowProps: ({ row }) => ({
        sx: {
          backgroundColor: row.getIsSelected()
            ? theme.palette.action.selected
            : row.index % 2 === 0
              ? "#f5f5f5"
              : "#fff",
        },
      }),

      renderTopToolbarCustomActions: () => (
        <CustomToolbar
          refetch={refetch}
          allCurrentlySelected={allCurrentlySelected}
          handleToggleSelectAll={handleToggleSelectAll}
          displayDataLength={displayData.length}
          showOnlySelected={showOnlySelected}
          setShowOnlySelected={setShowOnlySelected}
          selectedDataLength={selectedData.length}
          theme={theme}
        />
      ),
    })

    return <MaterialReactTable table={table} />
  },
)

const CustomTable = memo((props) => {
  const theme = useTheme()
  return (
    <div>
      <LargeScreenTable theme={theme} {...props} />
    </div>
  )
})

const CustomMultiRowSelectionAprobado = ({
  endpoint = "",
  endpointJson = {},
  errorMsgFilterSearch = "Error en cargar datos",
  queryKeyModal = "",
  perPage = 10,
  columnsTable = [],
  editableColumns = [],
  onHandleSelectedData,
}) => {
  const { data, isLoading, isError, refetch, isFetching } = useGetData()

  const [allRecords, setAllRecords] = useState([])

  // Inicialización optimizada de allRecords con conversión de fechas
  useEffect(() => {
    if (data && data.length > 0) {
      const initialRecords = data.map((item) => {
        const editableFields = {}
        editableColumns.forEach((column) => {
          const { accessorKey, defaultValue = "", type } = column
          let initialValue = item[accessorKey] || defaultValue

          // Convertir fechas al formato dd/mm/yyyy al inicializar
          if (type === "date" && initialValue) {
            initialValue = convertToDisplayDate(initialValue)
          }

          editableFields[accessorKey] = initialValue
        })

        return {
          originalData: { ...item },
          isSelected: true, // Por defecto todos seleccionados
          editableFields,
        }
      })
      setAllRecords(initialRecords)
    }
  }, [data, editableColumns])

  useEffect(() => {
    refetch()
  }, [refetch])

  const onItemSelected = useCallback((updatedRecords) => {
    setAllRecords(updatedRecords)
  }, [])

  // Función optimizada para actualizar un registro específico
  const onUpdateRecord = useCallback((index, updaterOrRecord) => {
    setAllRecords((prevRecords) => {
      const newRecords = [...prevRecords]

      if (typeof updaterOrRecord === "function") {
        newRecords[index] = updaterOrRecord(prevRecords[index])
      } else {
        newRecords[index] = updaterOrRecord
      }

      return newRecords
    })
  }, [])

  function useGetData() {
    return useQuery({
      queryKey: [queryKeyModal],
      queryFn: async () => {
        try {
          const response = await fetchwrapper(`${endpoint}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...endpointJson,
            }),
          })
          const result = await response.json()
          return result.data
        } catch (errorResponse) {
          Swal.fire({
            title: "Error",
            text: errorResponse?.details?.msg || "Error en la petición datos",
            icon: "error",
            confirmButtonText: "OK",
          })
          throw new Error(errorResponse?.details?.msg || "Error en la petición datos")
        }
      },
      keepPreviousData: false,
      retry: 1,
    })
  }

  const handleProcessAllRecords = useCallback(() => {
    if (allRecords && allRecords.length > 0) {
      onHandleSelectedData(allRecords)
    }
  }, [allRecords, onHandleSelectedData])

  const selectedCount = useMemo(() => allRecords.filter((r) => r.isSelected).length, [allRecords])

  return (
    <Box>
      <CustomBackdrop isLoading={isLoading || isFetching} />
      <div style={{ textAlign: "center", marginBottom: "15px" }}>
        <Button variant="contained" color="primary" onClick={handleProcessAllRecords}>
          Desaprobar pago
        </Button>

        <div style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Total: {allRecords.length} | Seleccionados: {selectedCount}
        </div>
      </div>

      <CustomTable
        data={isError ? [] : data || []}
        columnsTable={columnsTable || []}
        perPage={perPage}
        isLoading={isLoading}
        isError={isError}
        errorMsgFilterSearch={errorMsgFilterSearch}
        refetch={refetch}
        onItemSelected={onItemSelected}
        allRecords={allRecords}
        onUpdateRecord={onUpdateRecord}
        editableColumns={editableColumns}
      />
    </Box>
  )
}

export default CustomMultiRowSelectionAprobado
